import { findActual, type ResolvedActualDay } from "../../components/StaffTable/utils";
import type { Department } from "../../contexts/AuthProvider";
import type { NurseInShift } from "../../utils/models";
import { getShiftScheduledHours } from "../dailyPrintout/util/scheduledHours";
import { type AiPrintoutTimeContext, initialsOf, minutesFromDayStart, roleFields, textOrNull, timeFields } from "./aiPrintoutRowMappers";
import type { AiPrintoutShiftTimes, AiPrintoutUnmatchedShift } from "./contract";

/** A shift on the side `shifts` shows, and the other side's shift that stands for the same work. */
export type AiPrintoutCounterparts = {
	/** The other side's shift paired with a shown shift, or undefined when there is none. */
	counterpartOf: (shown: NurseInShift) => NurseInShift | undefined;
	/** The other side's shifts no shown shift is paired with. */
	unmatched: NurseInShift[];
	/** Actual rows that are a scheduled shift standing in for a punch (`ResolvedActualDay.substitutedShifts`). */
	isStandIn: (shift: NurseInShift) => boolean;
};

/**
 * Pairs the day's scheduled shifts with its actual side as the Day view's actual mode resolves it
 * (`resolveActualDayShifts`: punches, plus scheduled shifts standing in for staff who don't clock in). Each
 * scheduled shift takes the punch the CDPH 530's `findActual` picks for it (the same person's, by
 * `actualMatchesScheduled`, the most-overlapping of several) among the punches no earlier scheduled shift took,
 * so a punch stands for one scheduled shift at most. What's left over is the other side's unmatched: punches no
 * scheduled shift took, or — on an actual printout — the resolver's `missingFromScheduled`.
 */
export const pairCounterparts = (scheduled: NurseInShift[], actualDay: ResolvedActualDay, isActual: boolean): AiPrintoutCounterparts => {
	const actualBy = new Map<NurseInShift, NurseInShift>();
	const scheduledBy = new Map<NurseInShift, NurseInShift>();
	for (const scheduledShift of scheduled) {
		const punch = findActual(
			scheduledShift,
			actualDay.actualShifts.filter((candidate) => !scheduledBy.has(candidate)),
		);
		if (punch === undefined) continue;
		actualBy.set(scheduledShift, punch);
		scheduledBy.set(punch, scheduledShift);
	}
	const standIns = new Set(actualDay.substitutedShifts);
	return {
		counterpartOf: (shown) => (isActual ? scheduledBy : actualBy).get(shown),
		unmatched: isActual ? actualDay.missingFromScheduled : actualDay.actualShifts.filter((punch) => !scheduledBy.has(punch)),
		isStandIn: (shift) => standIns.has(shift),
	};
};

const missingPunchOf = (shift: NurseInShift): AiPrintoutShiftTimes["missingPunch"] => {
	if (shift.missingPunchIn && shift.missingPunchOut) return "both";
	if (shift.missingPunchIn) return "in";
	if (shift.missingPunchOut) return "out";
	return "none";
};

export interface ShiftTimesArgs {
	shift: NurseInShift;
	isStandIn: boolean;
	department: Department;
	jobManagementEnabled: boolean;
	context: AiPrintoutTimeContext;
}

/** One side of a shift, with the hours the Day Schedule staff table shows for it. */
export const shiftTimesOf = ({ shift, isStandIn, department, jobManagementEnabled, context }: ShiftTimesArgs): AiPrintoutShiftTimes => {
	const { start, end, timeLabel } = timeFields(shift.startTime, shift.endTime, context);
	return { start, end, timeLabel, hours: getShiftScheduledHours({ shift, department, jobManagementEnabled }), missingPunch: missingPunchOf(shift), isStandIn };
};

/** An other-side shift that no shown shift stands for. */
export const mapUnmatchedShift = (args: ShiftTimesArgs & { isActual: boolean }): AiPrintoutUnmatchedShift => {
	const { shift, isActual, context } = args;
	return {
		...shiftTimesOf(args),
		isActual,
		nurseId: shift.nurseId,
		name: shift.name,
		initials: initialsOf(shift.name),
		...roleFields(shift.role),
		location: textOrNull(shift.location),
		startMinutesFromDayStart: minutesFromDayStart(shift.startTime, context),
	};
};
